import type { FC, ReactNode, SetStateAction, Dispatch } from 'react';
import { createContext, useState, useMemo, useCallback } from 'react';

import { isValidYear } from '../utils/string-utils';

import type {
  HeatMapFilterOptions,
  HeatMapStatusFilterOption,
  HeatMapSexFilterOption,
  HeatMapRange,
  HeatMapCountryMetaData,
} from './map-data-types';
import { useMapData } from './use-map-data';
import { heatMapStatusMap } from './map-data-filter-maps';
import {
  NCCP_TIMELINE_START_YEAR,
  STATUS_TIMELINE_START_YEAR,
} from './heatmap-legend-config';
import { filterAppliesToViewMode } from './heatmap-filter-registry';

interface Props {
  children: ReactNode;
}
export interface HeatMapFilterConfig {
  continents: string[];
  nccpYearMin: number;
  nccpYearMax: number;
  statusYearMin: number;
  statusYearMax: number;
}

interface FilteredHeatMapData {
  // needs refactoring (e.g. generic filter interface)
  filterOptions: HeatMapFilterOptions;
  updateFilterOptions: (newOptions: HeatMapFilterOptions) => void;
  filterConfig: HeatMapFilterConfig;
  // basic filtering (should be above)
  rangeType: HeatMapRange;
  setRangeType: Dispatch<SetStateAction<HeatMapRange>>;
  filterTypeStatus: HeatMapStatusFilterOption;
  setFilterTypeStatus: Dispatch<SetStateAction<HeatMapStatusFilterOption>>;
  filterTypeSex: HeatMapSexFilterOption;
  setFilterTypeSex: Dispatch<SetStateAction<HeatMapSexFilterOption>>;
  resetFilterOptions: VoidFunction;
  filteredCountryMetaData: HeatMapCountryMetaData[];
  filteredCountryCount: number;
}
type FilteredHeatMapDataUndef = FilteredHeatMapData | undefined;
export const FilteredHeatMapDataContext =
  createContext<FilteredHeatMapDataUndef>(undefined);

/**
 * FilteredHeatMapDataProvider
 *
 * Hold state on all things filtered map data. This includes:
 * Options:
 *   filterOptions: filters as set by the user
 *   updateFilterOptions: setter for the filter options
 * FilterMapData:
 *   filteredHeatMapData: filtered heat map data
 *
 * Both states kept in same provider as they will always update together.
 */
export const FilteredHeatMapDataProvider: FC<Props> = ({ children }) => {
  const { countryHeatMapMetaData } = useMapData();
  const [filterOptions, setFilterOptions] = useState<HeatMapFilterOptions>({}); // needs refactoring
  const [rangeType, setRangeType] = useState<HeatMapRange>('incidence');
  const [filterTypeStatus, setFilterTypeStatus] =
    useState<HeatMapStatusFilterOption>('AllStatuses');
  const [filterTypeSex, setFilterTypeSex] =
    useState<HeatMapSexFilterOption>('Combined');

  const filterConfig: HeatMapFilterConfig = useMemo(() => {
    const currentYear = new Date().getFullYear();

    if (!countryHeatMapMetaData) {
      return {
        continents: [],
        nccpYearMin: NCCP_TIMELINE_START_YEAR,
        nccpYearMax: currentYear,
        statusYearMin: STATUS_TIMELINE_START_YEAR,
        statusYearMax: currentYear,
      };
    }

    const continents = new Set<string>();

    for (const country of Object.values(countryHeatMapMetaData)) {
      if (country.continent) {
        continents.add(country.continent);
      }
    }

    // Timeline always starts from 1997 - countries without previousNccpPublished
    // appear from this year with their basic status
    return {
      continents: [...continents].sort(),
      nccpYearMin: NCCP_TIMELINE_START_YEAR,
      nccpYearMax: currentYear,
      statusYearMin: STATUS_TIMELINE_START_YEAR,
      statusYearMax: currentYear,
    };
  }, [countryHeatMapMetaData]);

  // updates filter options with a merge so consumer does not need to know
  // context of current state to update
  const updateFilterOptions = useCallback(
    (newPartialFilterOptions: Partial<HeatMapFilterOptions>) => {
      setFilterOptions(prevOptions => ({
        ...prevOptions,
        ...newPartialFilterOptions,
      }));

      if (newPartialFilterOptions.status) {
        // Handle both single status and array of statuses
        const statusValue = Array.isArray(newPartialFilterOptions.status)
          ? newPartialFilterOptions.status[0] || 'AllStatuses'
          : newPartialFilterOptions.status;
        setFilterTypeStatus(statusValue);
      }
    },
    [setFilterOptions],
  );

  // Wrapper for setRangeType that clears timeline filters when leaving NCCP/status mode
  const handleSetRangeType = useCallback(
    (value: SetStateAction<HeatMapRange>) => {
      setRangeType(prevRange => {
        const newRange = typeof value === 'function' ? value(prevRange) : value;
        // Clear nccpYearMax filter when leaving NCCP mode
        if (prevRange === 'nccp' && newRange !== 'nccp') {
          setFilterOptions(prev => {
            const { nccpYearMax: _, ...rest } = prev;
            return rest;
          });
        }
        // Clear statusYearMax filter when leaving status mode
        if (prevRange === 'status' && newRange !== 'status') {
          setFilterOptions(prev => {
            const { statusYearMax: _, ...rest } = prev;
            return rest;
          });
        }
        return newRange;
      });
    },
    [],
  );

  const resetFilterOptions = useCallback(() => {
    setFilterOptions({});
    setFilterTypeStatus('AllStatuses');
    setFilterTypeSex('Combined');
    setRangeType('incidence');
  }, []);

  // Calculate filtered country metadata based on filters
  // Filter applicability is determined by heatmap-filter-view-modes.ts
  const filteredCountryMetaData = useMemo(() => {
    const countryMetaData = countryHeatMapMetaData
      ? Object.values(countryHeatMapMetaData)
      : [];

    let filtered = countryMetaData;

    // Status filter
    if (
      filterAppliesToViewMode('status', rangeType) &&
      filterTypeStatus &&
      filterTypeStatus !== heatMapStatusMap.all
    ) {
      filtered = filtered.filter(
        metaData =>
          heatMapStatusMap[metaData.countryStatus] === filterTypeStatus,
      );
    }

    // Region filter
    if (
      filterAppliesToViewMode('continent', rangeType) &&
      filterOptions.continent
    ) {
      filtered = filtered.filter(
        metaData => metaData.continent === filterOptions.continent,
      );
    }

    // NCCP filters
    if (
      filterAppliesToViewMode('nccpAvailable', rangeType) &&
      filterOptions.nccpAvailable
    ) {
      filtered = filtered.filter(
        metaData => metaData.nccpAvailable === filterOptions.nccpAvailable,
      );
    }

    if (
      filterAppliesToViewMode('lungCancerInNccp', rangeType) &&
      filterOptions.lungCancerInNccp
    ) {
      filtered = filtered.filter(
        metaData =>
          metaData.lungCancerInNccp === filterOptions.lungCancerInNccp,
      );
    }

    if (
      filterAppliesToViewMode('earlyDetectionInNccp', rangeType) &&
      filterOptions.earlyDetectionInNccp
    ) {
      filtered = filtered.filter(
        metaData =>
          metaData.earlyDetectionInNccp === filterOptions.earlyDetectionInNccp,
      );
    }

    if (
      filterAppliesToViewMode('fundingForEarlyDetection', rangeType) &&
      filterOptions.fundingForEarlyDetection
    ) {
      filtered = filtered.filter(
        metaData =>
          metaData.fundingForEarlyDetection ===
          filterOptions.fundingForEarlyDetection,
      );
    }

    // NCCP year filter
    if (
      filterAppliesToViewMode('nccpYearMax', rangeType) &&
      filterOptions.nccpYearMax !== undefined
    ) {
      filtered = filtered.filter(metaData => {
        const yearStr = metaData.previousNccpPublished;
        // Countries without a valid previousNccpPublished appear from 1997
        if (!isValidYear(yearStr)) {
          return filterOptions.nccpYearMax! >= NCCP_TIMELINE_START_YEAR;
        }
        const year = Number.parseInt(yearStr, 10);
        return year <= filterOptions.nccpYearMax!;
      });
    }

    // Status year filter
    if (
      filterAppliesToViewMode('statusYearMax', rangeType) &&
      filterOptions.statusYearMax !== undefined
    ) {
      filtered = filtered.filter(metaData => {
        const yearStr = metaData.currentCountryStatusYear;
        // Countries without a valid status year appear from the start
        if (!isValidYear(yearStr)) {
          return filterOptions.statusYearMax! >= STATUS_TIMELINE_START_YEAR;
        }
        const year = Number.parseInt(yearStr, 10);
        return year <= filterOptions.statusYearMax!;
      });
    }

    return filtered;
  }, [
    countryHeatMapMetaData,
    rangeType,
    filterTypeStatus,
    filterOptions.continent,
    filterOptions.nccpAvailable,
    filterOptions.lungCancerInNccp,
    filterOptions.earlyDetectionInNccp,
    filterOptions.fundingForEarlyDetection,
    filterOptions.nccpYearMax,
    filterOptions.statusYearMax,
  ]);

  // Derive count from filtered array
  const filteredCountryCount = filteredCountryMetaData.length;

  // memorize state object in case parent is re-rendered.
  const memorizedFilteredContextState = useMemo(
    () => ({
      updateFilterOptions,
      resetFilterOptions,
      filterOptions,
      filterConfig,
      filterTypeStatus,
      setRangeType: handleSetRangeType,
      rangeType,
      setFilterTypeStatus,
      filterTypeSex,
      setFilterTypeSex,
      filteredCountryMetaData,
      filteredCountryCount,
    }),
    [
      updateFilterOptions,
      resetFilterOptions,
      filterOptions,
      filterConfig,
      filterTypeStatus,
      handleSetRangeType,
      rangeType,
      filterTypeSex,
      filteredCountryMetaData,
      filteredCountryCount,
    ],
  );

  return (
    <FilteredHeatMapDataContext.Provider value={memorizedFilteredContextState}>
      {children}
    </FilteredHeatMapDataContext.Provider>
  );
};
